// Per-spool print menu — five document types. The Label option is
// disabled until the artwork lands.
import { useEffect, useRef, useState } from "react";
import { apiDownload } from "../../../../lib/api";

interface Props {
  jobId: number;
  spoolId: number;
  spoolNum: string;
  disabled?: boolean;
}

interface MenuKind {
  key: "job-card" | "cut-list" | "fittings-list" | "cut-fittings-list" | "time-sheet" | "label";
  label: string;
  icon: string;
}

const KINDS: MenuKind[] = [
  { key: "job-card", label: "A4 Job Card", icon: "📄" },
  { key: "cut-fittings-list", label: "Cut + Fittings Sheet", icon: "✂" },
  { key: "cut-list", label: "Cut List (full page)", icon: "✂" },
  { key: "fittings-list", label: "Fittings List (full page)", icon: "⚙" },
  { key: "time-sheet", label: "Time Sheet", icon: "⏱" },
  { key: "label", label: "100×150mm Label", icon: "🏷" },
];

export function SpoolPrintMenu({ jobId, spoolId, spoolNum, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const download = async (kind: MenuKind["key"], label: string) => {
    setBusy(kind);
    try {
      await apiDownload(
        `/api/tools/production-schedule/reports/job/${jobId}/spool/${spoolId}/${kind}.pdf`,
        `${spoolNum}_${kind}.pdf`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      window.alert(`${label} export failed: ${msg}`);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="btn-sm"
        title="Print exports for this spool"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        🖨 ▾
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            background: "var(--bsp-surface)",
            border: "1px solid var(--bsp-border)",
            borderRadius: 4,
            boxShadow: "var(--bsp-shadow-pop)",
            padding: 4,
            zIndex: 200,
            minWidth: 200,
          }}
        >
          {KINDS.map((k) => (
            <MenuItem
              key={k.key}
              label={`${k.icon} ${k.label}`}
              busy={busy === k.key}
              onClick={() => void download(k.key, k.label)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  busy,
  title,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  busy?: boolean;
  title?: string;
}) {
  const handleClick = disabled || busy || !onClick ? undefined : onClick;
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (handleClick && (e.key === "Enter" || e.key === " ")) handleClick();
      }}
      title={title}
      style={{
        padding: "7px 10px",
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 2,
        fontSize: 12,
        fontWeight: 600,
        color: disabled ? "var(--bsp-fg-muted)" : "var(--bsp-fg)",
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseOver={(e) => {
        if (!disabled) e.currentTarget.style.background = "var(--bsp-blue-pale)";
      }}
      onMouseOut={(e) => { e.currentTarget.style.background = ""; }}
    >
      {busy ? "Downloading…" : label}
    </div>
  );
}
